/**
 * What a person may open from a page header — which breadcrumbs are links,
 * whether the account name switches account — and the account switcher's
 * list.
 *
 * "All accounts" is for a system admin (every account) and for anyone whose
 * work spans two or more accounts they may see as a whole. An account's page
 * is the Accounts dashboard, so its crumb follows that dashboard's gate; a
 * location's page is the Locations dashboard's.
 */
import { useCallback, useMemo, useState } from 'react';
import { navTrailAccess, type ContextAccount, type User } from '@turn-connect/shared';
import { canShowExecutiveDashboard, getDashboardGates, isSystemAdmin as isSystemAdminCheck } from '@turn-connect/hooks';
import { useOptionalToast } from '@turn-connect/ui';
import { useWorkContexts } from '../../../queries/workContexts';
import { useAccountSummaries } from '../../../queries/adminReference';
import { useAccountSwitch } from '../../../hooks/useAccountSwitch';
import { navigateToAccountDashboard, navigateToAllAccounts } from '../../../utils/navigation';
import type { SearchOption } from '../../ui/filterOptionsByWords';
import type { TitleSwitchMenu } from './TitleSwitch';

export const ALL_ACCOUNTS_OPTION = '__all_accounts__';

export interface PlaceAccess {
  isSystemAdmin: boolean;
  canSeeAllAccounts: boolean;
  canOpenAccount: boolean;
  canOpenLocation: boolean;
  /** The accounts the person may see as a whole (non-admins; empty for an admin). */
  accounts: ContextAccount[];
  /** Still loading the person's accounts (non-admins): canSeeAllAccounts may still turn true. */
  loading: boolean;
}

export function usePlaceAccess(user: User | null | undefined, permissions: string[]): PlaceAccess {
  const admin = isSystemAdminCheck(permissions);
  const contextsQuery = useWorkContexts(user?.id, !!user && !admin);
  const contexts = contextsQuery.data;
  const accounts = useMemo(() => (contexts ?? []).filter((a) => a.can_view_account), [contexts]);
  const gates = getDashboardGates(user ?? null, permissions);
  // One rule for every crumb, the switcher and the Accounts page (shared with mobile).
  const trail = navTrailAccess({
    isSystemAdmin: admin,
    viewableAccountCount: accounts.length,
    canOpenAccountDashboard: canShowExecutiveDashboard(user ?? null, permissions),
    canOpenLocationDashboard: gates.canViewLocationDashboard,
  });
  return {
    isSystemAdmin: admin,
    ...trail,
    accounts,
    loading: !admin && !!user && contextsQuery.isLoading,
  };
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** The switcher's rows: "All accounts" first, then each account (with its location count where known). */
export function accountSwitchOptions(accounts: Array<{ id: string; name: string; locationCount?: number }>): SearchOption[] {
  return [
    { value: ALL_ACCOUNTS_OPTION, label: 'All accounts' },
    ...accounts.map((a) => ({
      value: a.id,
      label: a.name,
      ...(a.locationCount !== undefined && { detail: plural(a.locationCount, 'location', 'locations') }),
    })),
  ];
}

export interface AccountSwitchMenuOptions {
  user: User | null | undefined;
  permissions: string[];
  /** The account on screen. */
  accountId: string | null | undefined;
  /** Where a pick goes; defaults to that account's dashboard (or All accounts). */
  go?: { allAccounts: () => void; account: (accountId: string) => void };
  /**
   * Runs before anything moves — a settings page lets go first (asking about
   * unsaved edits), so the session never switches under an open page.
   */
  before?: (proceed: () => void) => void;
}

/**
 * The account name as a switcher, for a system admin or anyone with two or
 * more accounts; null otherwise. A non-admin's session belongs to one account,
 * so picking another moves the session there first (useAccountSwitch).
 */
export function useAccountSwitchMenu({ user, permissions, accountId, go, before }: AccountSwitchMenuOptions): TitleSwitchMenu | null {
  const access = usePlaceAccess(user, permissions);
  const admin = access.isSystemAdmin;
  const summaries = useAccountSummaries({ enabled: admin }).data;
  const accountSwitch = useAccountSwitch();
  const toast = useOptionalToast();
  const [busy, setBusy] = useState(false);

  const list = useMemo(
    () =>
      admin
        ? (summaries ?? []).map((a) => ({ id: a.id, name: a.name }))
        : access.accounts.map((a) => ({ id: a.id, name: a.name, locationCount: a.locations.length })),
    [admin, summaries, access.accounts]
  );
  const options = useMemo(() => accountSwitchOptions(list), [list]);

  const toAccount = go?.account ?? ((id: string) => navigateToAccountDashboard(id));
  const toAll = go?.allAccounts ?? navigateToAllAccounts;
  const onSelect = useCallback(
    (value: string) => {
      if (busy || value === accountId) return;
      const run = async () => {
        if (value === ALL_ACCOUNTS_OPTION) {
          toAll();
          return;
        }
        if (admin || !accountSwitch) {
          toAccount(value);
          return;
        }
        setBusy(true);
        const result = await accountSwitch.switchToAccount(value);
        setBusy(false);
        if (result.ok) toAccount(value);
        else toast?.error(result.error || 'Could not open that account');
      };
      if (before) before(() => void run());
      else void run();
    },
    [busy, accountId, admin, accountSwitch, toAccount, toAll, toast, before]
  );

  if (!access.canSeeAllAccounts || list.length === 0) return null;
  return {
    noun: 'account',
    options,
    value: accountId ?? '',
    onSelect,
    busy,
    testIdPrefix: 'account-switcher',
  };
}

/**
 * An account's name for a header or breadcrumb: from the system admin's
 * account list, or from the person's own accounts. Empty while unknown.
 */
export function useAccountName(user: User | null | undefined, permissions: string[], accountId: string | null | undefined): string {
  const access = usePlaceAccess(user, permissions);
  const summaries = useAccountSummaries({ enabled: access.isSystemAdmin && !!accountId }).data;
  if (!accountId) return '';
  if (access.isSystemAdmin) return summaries?.find((a) => a.id === accountId)?.name ?? '';
  return access.accounts.find((a) => a.id === accountId)?.name ?? '';
}
